import { Component, Inject, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { ClubService } from "../providers/club.service";
import { ProfileService } from "../providers/profile.service";
import { BvDialogService, BvDialogRef, BV_DIALOG_DATA } from "../design-system";
import {
  BvDialogTitleComponent,
  BvDialogBodyComponent,
  BvDialogFooterComponent,
} from "../design-system/primitives/dialog.component";
import { BvFormFieldComponent } from "../design-system/primitives/form-field.component";
import { BvInputDirective } from "../design-system/primitives/input.directive";
import { BvButtonComponent } from "../design-system/primitives/button.component";
import { BvCheckboxComponent } from "../design-system/primitives/checkbox.component";
import { BvIconComponent } from "../design-system/primitives/icon.component";
import { CloseDialogButtonComponent } from "../shared/close-dialog-button/close-dialog-button.component";
import { ConfirmDialogComponent } from "../shared/confirm-dialog/confirm-dialog.component";

/**
 * Add / edit a club location (venue). One dialog for both modes, keyed on
 * the injected `{ locationID }` like LeagueComponent. Every field is
 * optional — a location can be just "city, country" — so the only guard is
 * "not entirely blank". Named ClubLocation* because location.service.ts is
 * the geo/VAT lookup.
 *
 * Opened from the club settings dialog (and stacked on top of it), so it
 * closes itself via its own ref — never dialog.closeAll().
 */
@Component({
  selector: "app-club-location",
  templateUrl: "./club-location.component.html",
  styleUrls: ["./club-location.component.scss"],
  imports: [
    FormsModule,
    TranslateModule,
    BvDialogTitleComponent,
    BvDialogBodyComponent,
    BvDialogFooterComponent,
    BvFormFieldComponent,
    BvInputDirective,
    BvButtonComponent,
    BvCheckboxComponent,
    BvIconComponent,
    CloseDialogButtonComponent,
  ],
})
export class ClubLocationComponent implements OnInit {
  name = "";
  street = "";
  postalCode = "";
  city = "";
  state = "";
  country = "";
  telephone = "";
  mapURL = "";
  setHome = false;
  saving = false;

  constructor(
    public clubServ: ClubService,
    private profServ: ProfileService,
    private dialog: BvDialogService,
    private dialogRef: BvDialogRef<ClubLocationComponent>,
    private translate: TranslateService,
    @Inject(BV_DIALOG_DATA) public data?: { locationID?: string }
  ) {}

  get editing(): boolean {
    return !!this.data?.locationID;
  }
  get existing(): any {
    return this.editing
      ? this.clubServ.latestLocations.find(
          (l: any) => l.locationID === this.data!.locationID
        )
      : null;
  }
  get isBlank(): boolean {
    return ![
      this.name,
      this.street,
      this.postalCode,
      this.city,
      this.state,
      this.country,
      this.telephone,
    ].some((v) => v && v.trim());
  }
  /** State field only makes sense for US venues; keyed on the typed country. */
  get isUS(): boolean {
    return this.clubServ.isUnitedStates(this.country);
  }

  ngOnInit() {
    const loc = this.existing;
    if (loc) {
      this.name = loc.name || "";
      this.street = loc.street || "";
      this.postalCode = loc.postalCode || "";
      this.city = loc.city || "";
      this.state = loc.state || "";
      this.country = loc.country || "";
      this.telephone = loc.telephone || "";
      this.mapURL = loc.mapURL || "";
      this.setHome = this.clubServ.latestClub?.homeLocationID === loc.locationID;
    } else {
      // New venue: seed country (and US state) from the organizer's geo
      // profile data; they can overwrite either.
      const geo = this.profServ.latestProfile?.locationData;
      if (geo?.country) this.country = geo.country;
      if (geo?.countryCode === "US" && geo.state) this.state = geo.state;
    }
  }

  private fields() {
    return {
      name: this.name,
      street: this.street,
      postalCode: this.postalCode,
      city: this.city,
      // Never persist a state for a non-US country (stale after a country edit).
      state: this.isUS ? this.state : "",
      country: this.country,
      telephone: this.telephone,
      mapURL: this.mapURL,
    };
  }

  /** Helper link target built from the current (unsaved) address fields. */
  get mapsSearchURL(): string | null {
    return this.clubServ.locationMapsSearchURL(this.fields());
  }

  save() {
    if (this.isBlank || this.saving) return;
    this.saving = true;
    const op = this.editing
      ? this.clubServ.updateLocation(this.data!.locationID!, this.fields(), this.setHome)
      : this.clubServ.createLocation(this.fields(), this.setHome);
    op.finally(() => (this.saving = false));
    this.dialogRef.close();
  }

  delete() {
    const loc = this.existing;
    if (!loc) return;
    this.translate
      .get(["CONFIRM_DELETE", "CONFIRM", "CANCEL"])
      .subscribe((res: any) => {
        this.dialog
          .open(ConfirmDialogComponent, {
            data: {
              message: this.clubServ.locationLabel(loc, true),
              title: res["CONFIRM_DELETE"],
              acceptButton: res["CONFIRM"],
              cancelButton: res["CANCEL"],
            },
            panelClass: "confirm-dialog-container",
          })
          .afterClosed()
          .subscribe((accept: boolean) => {
            if (accept) {
              this.clubServ.deleteLocation(loc.locationID);
              this.dialogRef.close();
            }
          });
      });
  }
}
